/**
 * Notches Stress Test — what-if matrix for TT auto-trader open positions.
 *
 * Lives at /notches/auto-traders/stress-test. Reads from the impact-matrix
 * cache via getStressImpacts(); falls through to a fresh compute if stale.
 *
 * Layout: scenarios down, open trades across. Each cell shows BS and Greeks
 * projected PnL side-by-side, plus a trigger-status badge (hold/defend/close).
 *
 * See docs/wiki/decisions/2026-05-14-tt-stress-test.md
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Navigation } from '../Navigation';
import {
  notchesService,
  StressImpactRow,
  StressImpactsResponse,
  StressTriggerStatus,
  StressTriggerRecord,
  StressTriggerLevel,
  StressChannel,
  StressScenarioRecord,
  StressScenarioInput,
} from '../../services/notchesService';
import '../Dashboard.css';

const c = {
  bg: '#0f172a',
  card: '#1f2937',
  cardAlt: '#1a2332',
  border: '#374151',
  text: '#f9fafb',
  muted: '#9ca3af',
  dim: '#6b7280',
  green: '#16a34a',
  greenBg: '#16a34a20',
  amber: '#d97706',
  amberBg: '#d9770620',
  red: '#dc2626',
  redBg: '#dc262620',
  blue: '#3b82f6',
  blueBg: '#3b82f620',
};

// ── Tooltip helper ──────────────────────────────────────────────────────────
//
// Renders a small `?` icon with a hover-revealed tooltip. Click also toggles
// (so mobile users can tap to read the explanation).
const InfoTip: React.FC<{ text: string; width?: number }> = ({ text, width = 280 }) => {
  const [open, setOpen] = useState(false);
  return (
    <span style={{ position: 'relative', display: 'inline-block' }}>
      <span
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={() => setOpen(false)}
        onClick={(e) => { e.stopPropagation(); setOpen((o) => !o); }}
        style={{
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          width: 14, height: 14, borderRadius: '50%',
          background: c.cardAlt, color: c.muted, fontSize: 10, fontWeight: 700,
          marginLeft: 6, cursor: 'help', border: `1px solid ${c.border}`,
          userSelect: 'none', lineHeight: 1,
        }}
      >?</span>
      {open && (
        <span style={{
          // Anchor to the RIGHT of the `?` icon (not centered above). This
          // keeps the tooltip inside the form area on left-column labels,
          // so it can't get clipped by the fixed left sidebar.
          position: 'absolute', left: 'calc(100% + 8px)', top: '50%',
          transform: 'translateY(-50%)', padding: '8px 10px',
          background: '#0b1220', color: c.text, fontSize: 12,
          borderRadius: 4, border: `1px solid ${c.border}`,
          whiteSpace: 'normal', width, zIndex: 100,
          boxShadow: '0 4px 12px rgba(0,0,0,0.5)',
          pointerEvents: 'none', lineHeight: 1.45,
          textAlign: 'left', fontWeight: 400,
        }}>{text}</span>
      )}
    </span>
  );
};

const fmtMoney = (n: number | null) => {
  if (n == null || Number.isNaN(n)) return '—';
  const sign = n < 0 ? '-' : '';
  return `${sign}$${Math.abs(n).toFixed(0)}`;
};

const fmtSignedMoney = (n: number | null) => {
  if (n == null || Number.isNaN(n)) return '—';
  return `${n >= 0 ? '+' : ''}$${n.toFixed(0)}`;
};

const STATUS_COLOR: Record<StressTriggerStatus, { fg: string; bg: string; label: string }> = {
  hold: { fg: c.green, bg: c.greenBg, label: 'HOLD' },
  defend: { fg: c.amber, bg: c.amberBg, label: 'DEFEND' },
  close: { fg: c.red, bg: c.redBg, label: 'CLOSE' },
};

interface MatrixCell {
  row: StressImpactRow;
}

type TabKey = 'matrix' | 'scenarios' | 'triggers';

// ── Helpers ─────────────────────────────────────────────────────────────────

const fmtExp = (iso: string | null): string => {
  if (!iso) return '—';
  // iso is "YYYY-MM-DD" — render as "M/D"
  const m = iso.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return iso;
  return `${parseInt(m[2], 10)}/${parseInt(m[3], 10)}`;
};

const buildTradeLabel = (rows: StressImpactRow[], tradeId: string): { headline: string; sub: string } => {
  const r = rows.find((x) => x.tradeId === tradeId);
  if (!r) return { headline: tradeId.slice(0, 8), sub: '' };
  const k = r.shortStrike != null ? `${r.shortStrike.toFixed(0)}P` : '?';
  const strat = r.strategy === 'naked_put' ? 'naked' :
                r.strategy === 'credit_spread' && r.longStrike != null ? `${r.longStrike.toFixed(0)}P` :
                'spread';
  const qty = r.quantity ? `${r.quantity}× ` : '';
  return {
    headline: `${r.symbol} ${k}`,
    sub: `${qty}${strat} • exp ${fmtExp(r.expiration)}`,
  };
};

export const NotchesStressTest: React.FC = () => {
  const { token } = useAuth();
  const [tab, setTab] = useState<TabKey>('matrix');
  const [data, setData] = useState<StressImpactsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);

  const load = async (forceRefresh = false) => {
    if (!token) return;
    setError(null);
    if (forceRefresh) {
      setRefreshing(true);
    } else {
      setLoading(true);
    }
    try {
      const result = await notchesService.getStressImpacts(token, { refresh: forceRefresh });
      setData(result);
    } catch (err: any) {
      const msg = err?.response?.data?.error || err?.message || 'Failed to load stress impacts';
      setError(msg);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    load(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // Pivot rows: scenarios down, trades across.
  const { scenarios, trades, cellMap } = useMemo(() => {
    const rows = data?.rows || [];
    const scenMap = new Map<string, { key: string; name: string }>();
    const tradeMap = new Map<string, { tradeId: string; symbol: string; engineId: string }>();
    const cells = new Map<string, StressImpactRow>();
    for (const r of rows) {
      scenMap.set(r.scenarioKey, { key: r.scenarioKey, name: r.scenarioName || r.scenarioKey });
      tradeMap.set(r.tradeId, { tradeId: r.tradeId, symbol: r.symbol, engineId: r.engineId });
      cells.set(`${r.scenarioKey}#${r.tradeId}`, r);
    }
    return {
      scenarios: Array.from(scenMap.values()),
      trades: Array.from(tradeMap.values()),
      cellMap: cells,
    };
  }, [data]);

  const statusBadge = (s: StressTriggerStatus) => {
    const sc = STATUS_COLOR[s];
    return (
      <span style={{
        background: sc.bg, color: sc.fg, padding: '2px 6px', borderRadius: 4,
        fontSize: 10, fontWeight: 700, letterSpacing: 0.5,
      }}>{sc.label}</span>
    );
  };

  const pnlColor = (v: number | null) => {
    if (v == null) return c.muted;
    if (v > 0) return c.green;
    if (v < 0) return c.red;
    return c.text;
  };

  if (loading) {
    return (
      <div className="dashboard-container" style={{ background: c.bg, minHeight: '100vh', color: c.text }}>
        <Navigation />
        <div style={{ padding: 24 }}>Loading stress-test matrix…</div>
      </div>
    );
  }

  return (
    <div className="dashboard-container" style={{ background: c.bg, minHeight: '100vh', color: c.text }}>
      <Navigation />
      <div style={{ padding: '24px 32px', maxWidth: 1400, margin: '0 auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 8 }}>
          <h1 style={{ margin: 0, fontSize: 24 }}>Stress Test — TT Auto-Trader</h1>
          <button
            onClick={() => load(true)}
            disabled={refreshing}
            style={{
              background: c.blueBg, color: c.blue, border: `1px solid ${c.blue}`,
              padding: '6px 12px', borderRadius: 4, cursor: refreshing ? 'wait' : 'pointer',
              fontSize: 13,
            }}
          >
            {refreshing ? 'Recomputing…' : 'Recompute now'}
          </button>
        </div>
        <p style={{ color: c.muted, fontSize: 13, marginTop: 4, marginBottom: 16 }}>
          What happens to your open positions under synthetic market shocks.
          Each cell shows projected P&L by two methods: <strong>BS</strong> (full
          Black-Scholes reprice — ground truth) and <strong>Δ</strong> (Greeks-linear
          approximation — fast intuition). Big disagreement between the two means
          nonlinear risk (gamma, vega-of-vega) is dominating — trust BS.
        </p>

        {error && (
          <div style={{
            background: c.redBg, color: c.red, padding: 12, borderRadius: 4,
            border: `1px solid ${c.red}`, marginBottom: 16,
          }}>
            {error}
          </div>
        )}

        <div style={{ display: 'flex', gap: 4, marginBottom: 16, borderBottom: `1px solid ${c.border}` }}>
          {(['matrix', 'scenarios', 'triggers'] as TabKey[]).map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              style={{
                background: 'transparent',
                color: tab === t ? c.text : c.muted,
                border: 'none',
                borderBottom: tab === t ? `2px solid ${c.blue}` : '2px solid transparent',
                padding: '8px 16px',
                cursor: 'pointer',
                fontSize: 14,
                fontWeight: tab === t ? 600 : 400,
              }}
            >
              {t === 'matrix' ? 'Impact Matrix' : t === 'scenarios' ? 'What-if Scenarios' : 'Triggers'}
            </button>
          ))}
        </div>

        {tab === 'scenarios' && <ScenariosTab token={token} onChanged={() => load(true)} />}
        {tab === 'triggers' && <TriggersTab token={token} />}
        {tab === 'matrix' && (
          <>
        <div style={{ display: 'flex', gap: 16, marginBottom: 16, fontSize: 12, color: c.muted }}>
          <span>Cache: <span style={{ color: data?.cacheHit ? c.green : c.amber }}>
            {data?.cacheHit ? 'hit' : 'recomputed'}
          </span></span>
          {data?.computedAt && (
            <span>As of: <span style={{ color: c.text }}>
              {new Date(data.computedAt).toLocaleString('en-US', {
                timeZone: 'America/New_York', month: 'short', day: 'numeric',
                hour: 'numeric', minute: '2-digit',
              })} ET
            </span></span>
          )}
          <span>{trades.length} open position{trades.length !== 1 ? 's' : ''} × {scenarios.length} scenarios</span>
          {(() => {
            const regimes = Array.from(new Set((data?.rows || [])
              .map((r) => r.skewRegime).filter((x): x is string => !!x)));
            if (regimes.length === 0) return null;
            const color = regimes.some((r) => r === 'extreme') ? c.red :
                          regimes.some((r) => r === 'elevated') ? c.amber :
                          regimes.some((r) => r === 'cautious') ? c.blue : c.green;
            return (
              <span title="Current put-skew regime per underlying — drives the IV-shock amplification on far-OTM short legs">
                Skew: <span style={{ color, fontWeight: 600 }}>{regimes.join(' / ')}</span>
              </span>
            );
          })()}
        </div>

        {trades.length === 0 ? (
          <div style={{
            background: c.card, border: `1px solid ${c.border}`, borderRadius: 6,
            padding: 24, textAlign: 'center', color: c.muted,
          }}>
            No open TT options positions to stress-test. Open a position via the
            auto-trader, or come back when one fills.
          </div>
        ) : (
          <div style={{
            background: c.card, border: `1px solid ${c.border}`, borderRadius: 6,
            overflowX: 'auto',
          }}>
            <table style={{ borderCollapse: 'collapse', width: '100%', minWidth: 720 }}>
              <thead>
                <tr style={{ background: c.cardAlt }}>
                  <th style={{ ...thStyle, ...stickyLeftHeader }} title="Synthetic market shock applied to each position">Scenario</th>
                  {trades.map((t) => {
                    const lbl = buildTradeLabel(data?.rows || [], t.tradeId);
                    return (
                      <th key={t.tradeId} style={thStyle} title={`Trade ${t.tradeId}`}>
                        <div style={{ fontWeight: 600, color: c.text }}>{lbl.headline}</div>
                        <div style={{ fontSize: 10, color: c.muted, fontWeight: 400 }}>{lbl.sub}</div>
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {scenarios.map((s) => (
                  <tr key={s.key} style={{ borderTop: `1px solid ${c.border}` }}>
                    <td style={{ ...tdStyle, ...stickyLeftCell, fontWeight: 500 }}>
                      {s.name}
                    </td>
                    {trades.map((t) => {
                      const cell = cellMap.get(`${s.key}#${t.tradeId}`);
                      if (!cell) {
                        return (
                          <td key={t.tradeId} style={{ ...tdStyle, color: c.dim, textAlign: 'center' }}>—</td>
                        );
                      }
                      return (
                        <td key={t.tradeId} style={tdStyle}>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                            <div style={{ display: 'flex', gap: 8, alignItems: 'baseline' }}
                                 title="Black-Scholes full reprice — projected dollar P&L on this position under the shocked market inputs (ground truth)">
                              <span style={{ fontSize: 11, color: c.muted, width: 24 }}>BS</span>
                              <span style={{ color: pnlColor(cell.bsProjectedPnl), fontWeight: 600 }}>
                                {fmtSignedMoney(cell.bsProjectedPnl)}
                              </span>
                            </div>
                            <div style={{ display: 'flex', gap: 8, alignItems: 'baseline' }}
                                 title="Greeks-linear approximation — delta·ΔS + ½γΔS² + vega·Δσ − θ·Δt. Diverges from BS on large price moves (gamma) and large IV moves (vega-of-vega)">
                              <span style={{ fontSize: 11, color: c.muted, width: 24 }}>Δ</span>
                              <span style={{ color: pnlColor(cell.greeksProjectedPnl), fontSize: 12 }}>
                                {fmtSignedMoney(cell.greeksProjectedPnl)}
                              </span>
                            </div>
                            <div style={{ marginTop: 2 }}
                                 title="HOLD: loss within comfort. DEFEND: loss > 50% of max loss; review. CLOSE: loss > 80% of max loss; act now.">
                              {statusBadge(cell.triggerStatus)}
                            </div>
                            {cell.shockedUnderlying != null && (
                              <div style={{ fontSize: 10, color: c.dim, marginTop: 2 }}
                                   title="Projected underlying price and days-to-expiration after the scenario's price + time shocks">
                                S → {cell.shockedUnderlying.toFixed(0)}, DTE → {cell.shockedShortDte ?? '—'}
                              </div>
                            )}
                            {cell.amplifiedShortIvShock != null && cell.rawIvShock != null &&
                             cell.amplifiedShortIvShock !== cell.rawIvShock && (
                              <div style={{ fontSize: 10, color: c.amber, marginTop: 2 }}
                                   title={`Skew-amplified IV shock: raw ${(cell.rawIvShock*100).toFixed(0)} vol pts → applied ${(cell.amplifiedShortIvShock*100).toFixed(0)} vol pts.\nRegime: ${cell.skewRegime} (${cell.shortRegimeFactor?.toFixed(2)}x)\nLeg delta band: ${cell.shortDeltaFactor?.toFixed(2)}x`}>
                                IV: {(cell.rawIvShock*100).toFixed(0)}→{(cell.amplifiedShortIvShock*100).toFixed(0)} pts
                              </div>
                            )}
                          </div>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div style={{ marginTop: 24, color: c.muted, fontSize: 12 }}>
          <p style={{ margin: '4px 0' }}>
            <strong style={{ color: c.text }}>Trigger statuses:</strong>{' '}
            <span style={{ color: c.green }}>HOLD</span> — projected loss within
            comfort range. <span style={{ color: c.amber }}>DEFEND</span> —
            projected loss exceeds 50% of trade's max loss; review playbook.{' '}
            <span style={{ color: c.red }}>CLOSE</span> — projected loss exceeds
            80% of max loss; consider closing or rolling now.
          </p>
          <p style={{ margin: '4px 0' }}>
            BS reprice uses Black-Scholes-Merton; Greeks reprice uses delta + gamma
            + vega + theta from the trade's open snapshot. Greeks diverge from BS
            on large price moves (gamma) and large IV moves (vega-of-vega).
          </p>
        </div>
          </>
        )}
      </div>
    </div>
  );
};

// ── Triggers tab ──────────────────────────────────────────────────────────

const TriggersTab: React.FC<{ token: string | null }> = ({ token }) => {
  const [triggers, setTriggers] = useState<StressTriggerRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);

  const load = async () => {
    if (!token) return;
    setLoading(true);
    setErr(null);
    try {
      const r = await notchesService.listStressTriggers(token);
      setTriggers(r.triggers);
    } catch (e: any) {
      setErr(e?.response?.data?.error || e?.message || 'Failed to load triggers');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const onToggle = async (t: StressTriggerRecord) => {
    if (!token) return;
    try {
      await notchesService.updateStressTrigger(token, t.triggerId, { enabled: !t.enabled });
      load();
    } catch (e: any) {
      setErr(e?.response?.data?.error || 'Toggle failed');
    }
  };

  const onDelete = async (t: StressTriggerRecord) => {
    if (!token) return;
    if (!window.confirm(`Delete trigger "${t.name}"?`)) return;
    try {
      await notchesService.deleteStressTrigger(token, t.triggerId);
      load();
    } catch (e: any) {
      setErr(e?.response?.data?.error || 'Delete failed');
    }
  };

  const onExecute = async (t: StressTriggerRecord) => {
    if (!token) return;
    if (!window.confirm(`Record execute intent for "${t.name}"?\n\nv1: this records the intent — the auto-trader UI is where you complete the action.`)) return;
    try {
      const r = await notchesService.executeStressTrigger(token, t.triggerId);
      window.alert(r.message || 'Recorded.');
    } catch (e: any) {
      setErr(e?.response?.data?.error || 'Execute failed');
    }
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 12 }}>
        <h2 style={{ margin: 0, fontSize: 18 }}>Early-warning triggers</h2>
        <button
          onClick={() => setShowForm((s) => !s)}
          style={{
            background: c.blueBg, color: c.blue, border: `1px solid ${c.blue}`,
            padding: '6px 12px', borderRadius: 4, cursor: 'pointer', fontSize: 13,
          }}
        >
          {showForm ? 'Cancel' : '+ New trigger'}
        </button>
      </div>

      <p style={{ color: c.muted, fontSize: 13, marginTop: 0 }}>
        Configure rules that fire SMS/email/UI alerts when market conditions hit thresholds.
        Cron evaluates every 15 min, M–F, 9:30am–5pm ET. <strong>v1 safety:</strong> FULL_AUTO
        is auto-downgraded to SEMI_AUTO. No broker orders are auto-placed.
      </p>

      {showForm && <TriggerForm token={token} onCreated={() => { setShowForm(false); load(); }} />}

      {err && (
        <div style={{
          background: c.redBg, color: c.red, padding: 8, borderRadius: 4,
          border: `1px solid ${c.red}`, marginBottom: 12, fontSize: 13,
        }}>{err}</div>
      )}

      {loading ? (
        <div style={{ color: c.muted }}>Loading…</div>
      ) : triggers.length === 0 ? (
        <div style={{
          background: c.card, border: `1px solid ${c.border}`, borderRadius: 6,
          padding: 24, textAlign: 'center', color: c.muted,
        }}>No triggers yet. Click "+ New trigger" to set one up.</div>
      ) : (
        <div style={{
          background: c.card, border: `1px solid ${c.border}`, borderRadius: 6,
          overflowX: 'auto',
        }}>
          <table style={{ borderCollapse: 'collapse', width: '100%', minWidth: 720 }}>
            <thead>
              <tr style={{ background: c.cardAlt }}>
                <th style={{ ...thStyleLocal, ...stickyLeftHeader }}>Name</th>
                <th style={thStyleLocal}>Condition</th>
                <th style={thStyleLocal}>Level</th>
                <th style={thStyleLocal}>Channels</th>
                <th style={thStyleLocal}>Cooldown</th>
                <th style={thStyleLocal}>Last fired</th>
                <th style={thStyleLocal}></th>
              </tr>
            </thead>
            <tbody>
              {triggers.map((t) => (
                <tr key={t.triggerId} style={{ borderTop: `1px solid ${c.border}`, opacity: t.enabled ? 1 : 0.55 }}>
                  <td style={{ ...tdStyleLocal, ...stickyLeftCell }}>{t.name}</td>
                  <td style={{ ...tdStyleLocal, fontFamily: 'monospace', fontSize: 12, color: c.muted }}>{t.condition}</td>
                  <td style={tdStyleLocal}>{t.level}</td>
                  <td style={tdStyleLocal}>{(t.channels || []).join(', ') || '—'}</td>
                  <td style={tdStyleLocal}>{t.cooldownMinutes}m</td>
                  <td style={{ ...tdStyleLocal, color: c.dim, fontSize: 11 }}>
                    {t.lastFiredAt ? new Date(t.lastFiredAt).toLocaleString('en-US', {
                      timeZone: 'America/New_York', month: 'short', day: 'numeric',
                      hour: 'numeric', minute: '2-digit',
                    }) : '—'}
                  </td>
                  <td style={tdStyleLocal}>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button onClick={() => onToggle(t)} style={btnStyle(t.enabled ? c.amber : c.green)}>
                        {t.enabled ? 'Disable' : 'Enable'}
                      </button>
                      {t.level !== 'ALERT_ONLY' && (
                        <button onClick={() => onExecute(t)} style={btnStyle(c.blue)}>Execute</button>
                      )}
                      <button onClick={() => onDelete(t)} style={btnStyle(c.red)}>Delete</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

const TriggerForm: React.FC<{ token: string | null; onCreated: () => void }> = ({ token, onCreated }) => {
  const [name, setName] = useState('');
  const [condition, setCondition] = useState('SPX.intraday_change_pct < -3');
  const [level, setLevel] = useState<StressTriggerLevel>('ALERT_ONLY');
  const [channels, setChannels] = useState<StressChannel[]>(['ui']);
  const [cooldownMinutes, setCooldown] = useState(15);
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const submit = async () => {
    if (!token) return;
    setSubmitting(true);
    setErr(null);
    try {
      await notchesService.createStressTrigger(token, { name, condition, level, channels, cooldownMinutes });
      onCreated();
    } catch (e: any) {
      setErr(e?.response?.data?.error || e?.message || 'Failed to create');
    } finally {
      setSubmitting(false);
    }
  };

  const toggleChannel = (ch: StressChannel) => {
    setChannels((cs) => cs.includes(ch) ? cs.filter((x) => x !== ch) : [...cs, ch]);
  };

  return (
    <div style={{
      background: c.cardAlt, border: `1px solid ${c.border}`, borderRadius: 6,
      padding: 16, marginBottom: 16,
    }}>
      <h3 style={{ margin: '0 0 12px 0', fontSize: 15 }}>New trigger</h3>
      <div style={{ display: 'grid', gridTemplateColumns: '200px 1fr', gap: 10, alignItems: 'center' }}>
        <label style={lbl}>
          Name
          <InfoTip text="A short label so you can recognize this trigger in the list and in audit logs (e.g. 'SPX 3% intraday drop')." />
        </label>
        <input value={name} onChange={(e) => setName(e.target.value)} style={inp} placeholder="SPX drop 3%" />

        <label style={lbl}>
          Condition (DSL)
          <InfoTip width={380} text="The market condition that fires this trigger. Identifiers like SPX.intraday_change_pct, VIX.value, account.unrealized_pnl compared with < > <= >= == != and joined with AND / OR. Evaluated every 15 minutes during market hours by the watcher cron." />
        </label>
        <input value={condition} onChange={(e) => setCondition(e.target.value)} style={{ ...inp, fontFamily: 'monospace' }} />

        <label style={lbl}>
          Level
          <InfoTip width={380} text="ALERT_ONLY: just notify (default). SEMI_AUTO: notify + show one-click 'Execute' button in UI. FULL_AUTO: auto-execute playbook server-side. SAFETY: v1 auto-downgrades FULL_AUTO to SEMI_AUTO at fire time — no broker calls happen without explicit user confirmation." />
        </label>
        <select value={level} onChange={(e) => setLevel(e.target.value as StressTriggerLevel)} style={inp}>
          <option value="ALERT_ONLY">ALERT_ONLY — notify only</option>
          <option value="SEMI_AUTO">SEMI_AUTO — notify + one-click execute</option>
          <option value="FULL_AUTO">FULL_AUTO — auto-execute (v1: downgrades to SEMI_AUTO)</option>
        </select>

        <label style={lbl}>
          Channels
          <InfoTip text="Where to send the alert when this trigger fires. ui = appears in the Triggers tab + dashboard. sms = text message. email = email notification. Channels can be combined." />
        </label>
        <div style={{ display: 'flex', gap: 12 }}>
          {(['ui', 'sms', 'email'] as StressChannel[]).map((ch) => (
            <label key={ch} style={{ display: 'flex', gap: 4, alignItems: 'center', color: c.text, fontSize: 13, cursor: 'pointer' }}>
              <input type="checkbox" checked={channels.includes(ch)} onChange={() => toggleChannel(ch)} /> {ch}
            </label>
          ))}
        </div>

        <label style={lbl}>
          Cooldown (min)
          <InfoTip width={340} text="Minimum minutes between fires for this trigger. Prevents alert spam when a condition stays true. Range: 1 to 1440 (24h). Also enforced: max 3 fires per day per trigger." />
        </label>
        <input type="number" min={1} max={1440} value={cooldownMinutes}
          onChange={(e) => setCooldown(parseInt(e.target.value || '15', 10))} style={inp} />
      </div>

      <div style={{ marginTop: 12, color: c.muted, fontSize: 12 }}>
        <strong style={{ color: c.text }}>DSL syntax:</strong> identifiers (e.g. <code>SPX.intraday_change_pct</code>, <code>VIX.value</code>,
        <code> account.unrealized_pnl</code>) compared with <code>&lt; &gt; &lt;= &gt;= == !=</code> and joined with <code>AND</code>/<code>OR</code>.
        Parens supported. No arithmetic — keep one comparison per clause.
      </div>

      {err && (
        <div style={{
          background: c.redBg, color: c.red, padding: 8, borderRadius: 4, marginTop: 10, fontSize: 13,
        }}>{err}</div>
      )}

      <div style={{ marginTop: 12, display: 'flex', gap: 8 }}>
        <button onClick={submit} disabled={submitting || !name || !condition} style={{
          background: c.green, color: c.text, border: 'none', padding: '8px 16px',
          borderRadius: 4, cursor: submitting ? 'wait' : 'pointer', fontSize: 13, fontWeight: 600,
          opacity: (!name || !condition) ? 0.5 : 1,
        }}>{submitting ? 'Saving…' : 'Save trigger'}</button>
      </div>
    </div>
  );
};

// ── Scenarios tab ────────────────────────────────────────────────────────

const ScenariosTab: React.FC<{ token: string | null; onChanged: () => void }> = ({ token, onChanged }) => {
  const [scenarios, setScenarios] = useState<StressScenarioRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);

  const load = async () => {
    if (!token) return;
    setLoading(true);
    setErr(null);
    try {
      const r = await notchesService.listStressScenarios(token);
      setScenarios(r.scenarios);
    } catch (e: any) {
      setErr(e?.response?.data?.error || e?.message || 'Failed to load scenarios');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const onDelete = async (s: StressScenarioRecord) => {
    if (!token || s.readonly) return;
    if (!window.confirm(`Delete scenario "${s.name}"?`)) return;
    try {
      await notchesService.deleteStressScenario(token, s.scenarioId);
      load();
      onChanged();
    } catch (e: any) {
      setErr(e?.response?.data?.error || 'Delete failed');
    }
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 12 }}>
        <h2 style={{ margin: 0, fontSize: 18 }}>What-if scenarios</h2>
        <div style={{ display: 'flex', gap: 8 }}>
          <button
            onClick={load}
            style={{
              background: 'transparent', color: c.muted, border: `1px solid ${c.border}`,
              padding: '6px 12px', borderRadius: 4, cursor: 'pointer', fontSize: 13,
            }}
            title="Reload from server"
          >
            ⟳ Refresh
          </button>
          <button
            onClick={() => setShowForm((s) => !s)}
            style={{
              background: c.blueBg, color: c.blue, border: `1px solid ${c.blue}`,
              padding: '6px 12px', borderRadius: 4, cursor: 'pointer', fontSize: 13,
            }}
          >
            {showForm ? 'Cancel' : '+ New scenario'}
          </button>
        </div>
      </div>

      <p style={{ color: c.muted, fontSize: 13, marginTop: 0 }}>
        Each scenario is a parameterized market shock applied to every open position. The 8 default
        scenarios are read-only; custom ones you create are <strong>added</strong> to the matrix
        (not replacing the defaults). After creating or deleting a scenario, switch to the
        <strong> Impact Matrix</strong> tab and click <strong>Recompute now</strong> to see the new row.
      </p>

      {showForm && (
        <ScenarioForm token={token} onCreated={() => { setShowForm(false); load(); onChanged(); }} />
      )}

      {err && (
        <div style={{
          background: c.redBg, color: c.red, padding: 8, borderRadius: 4,
          border: `1px solid ${c.red}`, marginBottom: 12, fontSize: 13,
        }}>{err}</div>
      )}

      {loading ? (
        <div style={{ color: c.muted }}>Loading…</div>
      ) : (
        <div style={{
          background: c.card, border: `1px solid ${c.border}`, borderRadius: 6,
          overflowX: 'auto',
        }}>
          <table style={{ borderCollapse: 'collapse', width: '100%', minWidth: 720 }}>
            <thead>
              <tr style={{ background: c.cardAlt }}>
                <th style={{ ...thStyleLocal, ...stickyLeftHeader }}>Name</th>
                <th style={thStyleLocal} title="Underlying price change applied to spot">Price shock</th>
                <th style={thStyleLocal} title="IV change in vol points (0.10 = +10 vol pts)">IV shock</th>
                <th style={thStyleLocal} title="Days to advance forward (time decay applied)">Days fwd</th>
                <th style={thStyleLocal} title="VIX shock in points (informational; not used in per-leg reprice)">VIX shock</th>
                <th style={thStyleLocal}>Source</th>
                <th style={thStyleLocal}></th>
              </tr>
            </thead>
            <tbody>
              {scenarios.map((s) => (
                <tr key={s.scenarioId} style={{ borderTop: `1px solid ${c.border}` }}>
                  <td style={{ ...tdStyleLocal, ...stickyLeftCell }}>
                    <div style={{ fontWeight: 500 }}>{s.name}</div>
                    {s.description && (
                      <div style={{ fontSize: 11, color: c.muted, marginTop: 2 }}>{s.description}</div>
                    )}
                  </td>
                  <td style={{ ...tdStyleLocal, color: s.params.price_shock_pct < 0 ? c.red : s.params.price_shock_pct > 0 ? c.green : c.muted }}>
                    {(s.params.price_shock_pct * 100).toFixed(1)}%
                  </td>
                  <td style={tdStyleLocal}>
                    {s.params.iv_shock_pts >= 0 ? '+' : ''}{(s.params.iv_shock_pts * 100).toFixed(0)} vol pts
                  </td>
                  <td style={tdStyleLocal}>{s.params.days_forward}d</td>
                  <td style={tdStyleLocal}>{s.params.vix_shock_pts != null ? `${s.params.vix_shock_pts >= 0 ? '+' : ''}${s.params.vix_shock_pts}` : '—'}</td>
                  <td style={{ ...tdStyleLocal, color: c.muted, fontSize: 11 }}>
                    {s.readonly ? 'library' : 'custom'}
                  </td>
                  <td style={tdStyleLocal}>
                    {!s.readonly && (
                      <button onClick={() => onDelete(s)} style={btnStyle(c.red)}>Delete</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

const ScenarioForm: React.FC<{ token: string | null; onCreated: () => void }> = ({ token, onCreated }) => {
  const [aiPrompt, setAiPrompt] = useState('');
  const [aiLoading, setAiLoading] = useState(false);
  const [aiReasoning, setAiReasoning] = useState<string | null>(null);

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [pricePct, setPricePct] = useState('-3');     // user enters as %, e.g. -3 for -3%
  const [ivVol, setIvVol] = useState('5');             // user enters as vol pts, e.g. 5 for +5 vol pts
  const [daysFwd, setDaysFwd] = useState('1');
  const [vixPts, setVixPts] = useState('5');
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const aiSuggest = async () => {
    if (!token || !aiPrompt.trim()) return;
    setAiLoading(true);
    setErr(null);
    setAiReasoning(null);
    try {
      const r = await notchesService.suggestStressScenario(token, aiPrompt.trim());
      const s = r.suggestion;
      setName(s.name);
      setDescription(s.description);
      // params from API are decimals; the form inputs are in %/vol-pts/days
      setPricePct((s.params.price_shock_pct * 100).toString());
      setIvVol((s.params.iv_shock_pts * 100).toString());
      setDaysFwd(s.params.days_forward.toString());
      setVixPts((s.params.vix_shock_pts ?? 0).toString());
      setAiReasoning(s.reasoning);
    } catch (e: any) {
      setErr(e?.response?.data?.error || e?.message || 'AI suggest failed');
    } finally {
      setAiLoading(false);
    }
  };

  const submit = async () => {
    if (!token) return;
    setSubmitting(true);
    setErr(null);
    try {
      const input: StressScenarioInput = {
        name,
        description,
        params: {
          price_shock_pct: Number(pricePct) / 100,
          iv_shock_pts: Number(ivVol) / 100,
          days_forward: parseInt(daysFwd, 10),
          vix_shock_pts: Number(vixPts),
        },
      };
      await notchesService.createStressScenario(token, input);
      onCreated();
    } catch (e: any) {
      setErr(e?.response?.data?.error || e?.message || 'Failed to create');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={{
      background: c.cardAlt, border: `1px solid ${c.border}`, borderRadius: 6,
      padding: 16, marginBottom: 16,
    }}>
      <h3 style={{ margin: '0 0 12px 0', fontSize: 15 }}>New scenario</h3>

      {/* AI suggest box — describe in plain English, Claude infers params */}
      <div style={{
        background: c.bg, border: `1px solid ${c.blue}`, borderRadius: 6,
        padding: 12, marginBottom: 16,
      }}>
        <div style={{ fontSize: 12, color: c.blue, fontWeight: 600, marginBottom: 6 }}>
          ✨ DESCRIBE IT IN PLAIN ENGLISH (optional)
          <InfoTip width={320} text="Describe a scenario in plain English (e.g. 'tariff war reescalation' or 'Powell surprise 50bp hike'). Claude Haiku translates it into the four shock parameters. The fields below auto-fill — review and tweak before saving." />
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <input
            value={aiPrompt}
            onChange={(e) => setAiPrompt(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') aiSuggest(); }}
            style={{ ...inp, flex: 1 }}
            placeholder="e.g. surprise 50bp Fed hike Wednesday  •  tariff shock from China  •  pure 3-day theta"
            disabled={aiLoading}
          />
          <button
            onClick={aiSuggest}
            disabled={aiLoading || !aiPrompt.trim()}
            style={{
              background: c.blue, color: c.text, border: 'none',
              padding: '6px 14px', borderRadius: 4, fontSize: 13, fontWeight: 600,
              cursor: aiLoading ? 'wait' : 'pointer',
              opacity: !aiPrompt.trim() ? 0.5 : 1,
            }}
          >
            {aiLoading ? 'Thinking…' : 'Suggest'}
          </button>
        </div>
        {aiReasoning && (
          <div style={{
            marginTop: 10, padding: 8, background: c.cardAlt, borderRadius: 4,
            fontSize: 12, color: c.muted, fontStyle: 'italic',
          }}>
            <strong style={{ color: c.text, fontStyle: 'normal' }}>AI reasoning:</strong> {aiReasoning}
            <div style={{ marginTop: 6, color: c.dim }}>
              Parameters filled below — review and tweak before saving.
            </div>
          </div>
        )}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '200px 1fr', gap: 10, alignItems: 'center' }}>
        <label style={lbl}>
          Name
          <InfoTip text="A short label (e.g. 'Fed surprise hike'). Appears as the row label in the Impact Matrix, so keep it scannable." />
        </label>
        <input value={name} onChange={(e) => setName(e.target.value)} style={inp} placeholder="Fed surprise hike" />

        <label style={lbl}>
          Description (optional)
          <InfoTip text="Free-text note explaining what this scenario represents. Not used by the math — purely for your own future reference." />
        </label>
        <input value={description} onChange={(e) => setDescription(e.target.value)} style={inp} placeholder="50bp surprise; rates up, equities down" />

        <label style={lbl}>
          Price shock (%)
          <InfoTip width={340} text="Percentage change applied to the underlying spot price. -5 = down 5%; +5 = up 5%. Range: -50 to +50. The repricer uses this to shock S in the Black-Scholes formula." />
        </label>
        <input type="number" step="0.5" value={pricePct} onChange={(e) => setPricePct(e.target.value)} style={inp} />

        <label style={lbl}>
          IV shock (vol pts)
          <InfoTip width={360} text="Change in implied volatility in absolute vol points. 5 = IV up 5 vol pts (e.g. 18% → 23%). Range: -50 to +100. This is amplified per-leg by the current skew regime (elevated = 1.35x, extreme = 1.65x) and the leg's |delta| band — far-OTM short puts get hit harder than ATM ones." />
        </label>
        <input type="number" step="1" value={ivVol} onChange={(e) => setIvVol(e.target.value)} style={inp} />

        <label style={lbl}>
          Days forward
          <InfoTip width={340} text="Number of calendar days to advance the clock. Theta accrues (favors short premium); DTE shrinks (favors short premium). Set to 0 for instant shock with no time passage. Set to 999 for 'hold to expiration' (clamped to actual DTE per leg)." />
        </label>
        <input type="number" step="1" min="0" max="365" value={daysFwd} onChange={(e) => setDaysFwd(e.target.value)} style={inp} />

        <label style={lbl}>
          VIX shock (pts)
          <InfoTip width={340} text="VIX index change in absolute points. INFORMATIONAL ONLY — not used in per-leg reprice math (BS prices each leg's IV independently). Useful for documenting what kind of vol environment the scenario represents." />
        </label>
        <input type="number" step="1" value={vixPts} onChange={(e) => setVixPts(e.target.value)} style={inp} />
      </div>

      <div style={{ marginTop: 12, color: c.muted, fontSize: 12 }}>
        Example: "Fed surprise hike" → Price -3%, IV +5 vol pts, Days 1, VIX +5.
        The next time you recompute the Impact Matrix, every open position will be repriced under this scenario.
      </div>

      {err && (
        <div style={{
          background: c.redBg, color: c.red, padding: 8, borderRadius: 4, marginTop: 10, fontSize: 13,
        }}>{err}</div>
      )}

      <div style={{ marginTop: 12, display: 'flex', gap: 8 }}>
        <button onClick={submit} disabled={submitting || !name} style={{
          background: c.green, color: c.text, border: 'none', padding: '8px 16px',
          borderRadius: 4, cursor: submitting ? 'wait' : 'pointer', fontSize: 13, fontWeight: 600,
          opacity: !name ? 0.5 : 1,
        }}>{submitting ? 'Saving…' : 'Save scenario'}</button>
      </div>
    </div>
  );
};

const thStyleLocal: React.CSSProperties = {
  textAlign: 'left', padding: '10px 12px', borderBottom: `1px solid ${c.border}`,
  fontSize: 12, color: c.muted, fontWeight: 500, whiteSpace: 'nowrap',
};
const tdStyleLocal: React.CSSProperties = {
  padding: '10px 12px', fontSize: 13, verticalAlign: 'top', whiteSpace: 'nowrap',
};
const btnStyle = (color: string): React.CSSProperties => ({
  background: 'transparent', color, border: `1px solid ${color}`,
  padding: '3px 8px', borderRadius: 3, cursor: 'pointer', fontSize: 11,
});
const lbl: React.CSSProperties = { color: c.muted, fontSize: 13 };
const inp: React.CSSProperties = {
  background: c.bg, color: c.text, border: `1px solid ${c.border}`,
  padding: '6px 10px', borderRadius: 4, fontSize: 13, width: '100%',
};

const thStyle: React.CSSProperties = {
  textAlign: 'left',
  padding: '10px 12px',
  borderBottom: `1px solid ${c.border}`,
  fontSize: 12,
  color: c.muted,
  fontWeight: 500,
  whiteSpace: 'nowrap',
};

const tdStyle: React.CSSProperties = {
  padding: '10px 12px',
  fontSize: 13,
  verticalAlign: 'top',
  whiteSpace: 'nowrap',
};

// Make the leftmost column "stick" while the rest scroll horizontally.
// Background must be opaque so the scrolled-under content doesn't bleed through.
const stickyLeftHeader: React.CSSProperties = {
  position: 'sticky',
  left: 0,
  background: c.cardAlt,
  zIndex: 2,
  borderRight: `1px solid ${c.border}`,
};

const stickyLeftCell: React.CSSProperties = {
  position: 'sticky',
  left: 0,
  background: c.cardAlt,
  zIndex: 1,
  borderRight: `1px solid ${c.border}`,
};
